import {
  Box,
  Button,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import { useState } from "react";
import { compactFieldSx } from "./formStyles";
import type { ModuleBuilderDraft, InstrumentDraft } from "./types";

type Props = {
  draft: ModuleBuilderDraft;
  onSelectInstrument: (instrumentId: string) => void;
  onAddInstrument: () => void;
  onRemoveInstrument: (instrumentId: string) => void;
  onModuleTitleChange: (title: string) => void;
  onCollapse: () => void;
  activityCountByInstrumentId: Record<string, number>;
};

export function OutlineRail({
  draft,
  onSelectInstrument,
  onAddInstrument,
  onRemoveInstrument,
  onModuleTitleChange,
  onCollapse,
  activityCountByInstrumentId
}: Props) {
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(draft.moduleTitle);

  const commitTitle = () => {
    onModuleTitleChange(titleDraft.trim());
    setEditingTitle(false);
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        position: { lg: "sticky" },
        top: { lg: 16 },
        borderRadius: 2,
        minHeight: 360
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="overline" color="text.secondary">
          Outline
        </Typography>
        <Tooltip title="Hide outline">
          <IconButton size="small" onClick={onCollapse}>
            <MenuOpenIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      <Box
        sx={{
          py: 0.5,
          px: 1,
          borderRadius: 1,
          fontWeight: 600,
          fontSize: "0.875rem",
          mb: 1
        }}
      >
        {editingTitle ? (
          <TextField
            size="small"
            autoFocus
            fullWidth
            value={titleDraft}
            onChange={(e) => setTitleDraft(e.target.value)}
            onBlur={commitTitle}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitTitle();
              if (e.key === "Escape") {
                setTitleDraft(draft.moduleTitle);
                setEditingTitle(false);
              }
            }}
            placeholder="Untitled module"
            inputProps={{ "aria-label": "Module title" }}
            sx={compactFieldSx}
          />
        ) : (
          <Box
            onClick={() => {
              setTitleDraft(draft.moduleTitle);
              setEditingTitle(true);
            }}
            sx={{ cursor: "text", color: draft.moduleTitle ? "text.primary" : "text.secondary" }}
          >
            {draft.moduleTitle.trim() || "Untitled module"}
          </Box>
        )}
      </Box>

      <Divider sx={{ mb: 1 }} />

      <Stack spacing={0.25}>
        {draft.instruments.map((instr) => (
          <InstrumentNode
            key={instr.id}
            instr={instr}
            active={instr.id === draft.activeInstrumentId}
            activityCount={activityCountByInstrumentId[instr.id] ?? 0}
            questionCount={(draft.instrumentQuestionIdsById[instr.id] ?? []).length}
            canRemove={draft.instruments.length > 1}
            onClick={() => onSelectInstrument(instr.id)}
            onRemove={() => onRemoveInstrument(instr.id)}
          />
        ))}
      </Stack>

      <Divider sx={{ my: 1.5 }} />

      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={onAddInstrument}
        sx={{ width: "100%", justifyContent: "flex-start" }}
      >
        Add instrument
      </Button>
    </Paper>
  );
}

function InstrumentNode({
  instr,
  active,
  activityCount,
  questionCount,
  canRemove,
  onClick,
  onRemove
}: {
  instr: InstrumentDraft;
  active: boolean;
  activityCount: number;
  questionCount: number;
  canRemove: boolean;
  onClick: () => void;
  onRemove: () => void;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        px: 1,
        py: 0.75,
        borderRadius: 1,
        bgcolor: active ? "action.selected" : "transparent",
        cursor: active ? "default" : "pointer",
        "&:hover .outline-row-actions": { opacity: 1 },
        "&:hover": { bgcolor: active ? "action.selected" : "action.hover" }
      }}
      onClick={active ? undefined : onClick}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: active ? 600 : 500,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis"
          }}
        >
          {instr.title.trim() || "Untitled instrument"}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {activityCount} act · {questionCount} q
        </Typography>
      </Box>
      {canRemove && (
        <Tooltip title="Remove instrument">
          <IconButton
            className="outline-row-actions"
            size="small"
            sx={{ opacity: 0, transition: "opacity 0.15s" }}
            onClick={(e) => {
              e.stopPropagation();
              if (window.confirm(`Remove "${instr.title || "instrument"}"? This cannot be undone.`)) {
                onRemove();
              }
            }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}
